/**
 * LHD — co-located TanStack Query hooks. Query keys are namespaced under 'lhd'
 * and scoped by the entered workspace where the data is workspace-tier, so cache
 * entries never leak across entered workspaces. Mutations invalidate the lists
 * they affect and bubble 409 conflicts to the caller for the conflict toast.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { lhdApi } from './api';
import type {
  AgentListQuery,
  AttentionListQuery,
  ConversationListQuery,
  CreateAgentInput,
  MatchTriageInput,
  TestHarnessExample,
  TriageListQuery,
  UpdateAgentInput,
  ScriptListQuery,
  CreateScriptInput,
  UpdateScriptInput,
  CreateStageInput,
  UpdateStageInput,
  CreateBranchInput,
  UpdateBranchInput,
  AgentDocumentListQuery,
  CreateAgentDocumentMeta,
  UpdateAgentDocumentInput,
  BookingListQuery,
  LogBookingOutcomeInput,
} from './types';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------
export const lhdKeys = {
  conversations: (ws: string, q: ConversationListQuery) => ['lhd', 'conversations', ws, q] as const,
  conversation: (ws: string, id: string) => ['lhd', 'conversation', ws, id] as const,
  inboxFacets: (ws: string) => ['lhd', 'inboxFacets', ws] as const,
  messages: (ws: string, id: string) => ['lhd', 'messages', ws, id] as const,
  attention: (ws: string, q: AttentionListQuery) => ['lhd', 'attention', ws, q] as const,
  attentionBadge: (ws: string) => ['lhd', 'attention-badge', ws] as const,
  attentionCard: (ws: string, id: string) => ['lhd', 'attentionCard', ws, id] as const,
  agents: (ws: string, q: AgentListQuery) => ['lhd', 'agents', ws, q] as const,
  agent: (ws: string, id: string) => ['lhd', 'agent', ws, id] as const,
  testRuns: (ws: string, agentId: string) => ['lhd', 'testRuns', ws, agentId] as const,
  testRun: (ws: string, agentId: string, id: string) => ['lhd', 'testRun', ws, agentId, id] as const,
  triage: (ws: string, q: TriageListQuery) => ['lhd', 'triage', ws, q] as const,
  inboxes: (workspaceId: string) => ['lhd', 'inboxes', workspaceId] as const,
  reps: (workspaceId: string) => ['lhd', 'reps', workspaceId] as const,
  scripts: (ws: string, q: ScriptListQuery) => ['lhd', 'scripts', ws, q] as const,
  script: (ws: string, id: string) => ['lhd', 'script', ws, id] as const,
  documents: (ws: string, q: AgentDocumentListQuery) => ['lhd', 'documents', ws, q] as const,
  document: (ws: string, id: string) => ['lhd', 'document', ws, id] as const,
  decisions: (ws: string, conversationId: string) => ['lhd', 'decisions', ws, conversationId] as const,
  bookings: (ws: string, q: BookingListQuery) => ['lhd', 'bookings', ws, q] as const,
  booking: (ws: string, id: string) => ['lhd', 'booking', ws, id] as const,
};

// ---------------------------------------------------------------------------
// Workspace Inbox + Conversation thread
// ---------------------------------------------------------------------------
export function useConversations(ws: string, q: ConversationListQuery) {
  return useQuery({
    queryKey: lhdKeys.conversations(ws, q),
    queryFn: () => lhdApi.listConversations(q),
    placeholderData: (prev) => prev,
    // Near-real-time: surface new inbound replies / drafts without a manual reload.
    refetchInterval: 7000,
    refetchOnWindowFocus: true,
  });
}

export function useConversation(ws: string, id: string | null) {
  return useQuery({
    queryKey: lhdKeys.conversation(ws, id ?? '∅'),
    queryFn: () => lhdApi.getConversation(id as string),
    enabled: !!id,
    // Collaborative thread: poll fast so another AM's reply / approval / takeover (and the lead's
    // new replies + fresh drafts) appear for EVERYONE viewing within a few seconds, not on reload.
    refetchInterval: 3000,
    refetchOnWindowFocus: true,
  });
}

/**
 * The live per-turn processing status for the open conversation (read → decide → write → 4 gates →
 * done / retry / failed). Polled fast so the agent's work appears step-by-step, like a chat that types.
 */
export function useTurnStatus(ws: string, id: string | null) {
  return useQuery({
    queryKey: ['lhd', 'turnStatus', ws, id ?? '∅'] as const,
    queryFn: () => lhdApi.turnStatus(id as string),
    enabled: !!id,
    refetchInterval: 1200,
    refetchOnWindowFocus: true,
  });
}

/** Distinct campaigns/agents used by this workspace's conversations — drives the inbox filter selects. */
export function useInboxFacets(ws: string) {
  return useQuery({
    queryKey: lhdKeys.inboxFacets(ws),
    queryFn: () => lhdApi.inboxFacets(),
    placeholderData: (prev) => prev,
  });
}

export function useComposeMessage(ws: string, conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => lhdApi.composeMessage(conversationId, body),
    // onSettled: refresh even if the send failed, so the thread reflects server truth either way.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: lhdKeys.conversation(ws, conversationId) });
      void qc.invalidateQueries({ queryKey: ['lhd', 'conversations', ws] });
    },
  });
}

/** One-shot AI grammar/typo/capitalization fix for the draft being edited (English only). */
export function useGrammarCheck(_ws: string) {
  return useMutation({
    mutationFn: (text: string) => lhdApi.grammarCheck(text),
  });
}

function useConversationAction(
  ws: string,
  fn: (id: string, version: number) => Promise<unknown>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) => fn(id, version),
    // onSettled (not onSuccess): re-sync after BOTH outcomes so a lost optimistic-lock race (409 —
    // someone else took over / resolved first) immediately refreshes the loser's thread to the
    // winner's result instead of leaving them on a stale version.
    onSettled: (_d, _e, { id }) => {
      void qc.invalidateQueries({ queryKey: lhdKeys.conversation(ws, id) });
      void qc.invalidateQueries({ queryKey: ['lhd', 'conversations', ws] });
    },
  });
}

export const useTakeOver = (ws: string) => useConversationAction(ws, lhdApi.takeOver);
export const useHandBack = (ws: string) => useConversationAction(ws, lhdApi.handBack);
export const useHandoff = (ws: string) => useConversationAction(ws, lhdApi.handoff);
export const useTakeOverFromRep = (ws: string) => useConversationAction(ws, lhdApi.takeOverFromRep);

export function useAssignConversation(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, amUserId }: { id: string; version: number; amUserId: string }) =>
      lhdApi.assignConversation(id, version, amUserId),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: lhdKeys.conversation(ws, id) });
      void qc.invalidateQueries({ queryKey: ['lhd', 'conversations', ws] });
    },
  });
}

export function useCloseConversation(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, reason }: { id: string; version: number; reason: string }) =>
      lhdApi.closeConversation(id, version, reason),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: lhdKeys.conversation(ws, id) });
      void qc.invalidateQueries({ queryKey: ['lhd', 'conversations', ws] });
    },
  });
}

export function useSetDisposition(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, disposition }: { id: string; version: number; disposition: string }) =>
      lhdApi.setDisposition(id, version, disposition),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: lhdKeys.conversation(ws, id) });
    },
  });
}

// ---------------------------------------------------------------------------
// Admin "System actions" (#6) — re-run agent + raw AI decision view
// ---------------------------------------------------------------------------

/** Re-run the agent on the conversation's latest inbound reply; refresh thread + queues. */
export function useRerunAgent(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => lhdApi.rerunAgent(id),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: lhdKeys.conversation(ws, id) });
      void qc.invalidateQueries({ queryKey: ['lhd', 'conversations', ws] });
      void qc.invalidateQueries({ queryKey: ['lhd', 'attention', ws] });
    },
  });
}

/** Raw model-decision rows for a conversation (admin debugging). Disabled until opened. */
export function useConversationDecisions(
  ws: string,
  conversationId: string,
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: lhdKeys.decisions(ws, conversationId),
    queryFn: () => lhdApi.listConversationDecisions(ws, conversationId),
    enabled: opts?.enabled ?? !!conversationId,
  });
}

// ---------------------------------------------------------------------------
// Attention Queue
// ---------------------------------------------------------------------------
/**
 * Count behind the Inbox nav badge. Its own endpoint, not `useAttentionCards(...).total` — the badge
 * counts only drafts awaiting approval, whereas the queue lists every open card. Sharing the list
 * total is what made the badge read 16 while nothing was actually waiting on a reply.
 */
export function useAttentionBadge(ws: string, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: lhdKeys.attentionBadge(ws),
    queryFn: () => lhdApi.attentionNotificationCount(),
    placeholderData: (prev) => prev,
    // Same cadence as the queue: a card another AM just resolved should stop badging promptly.
    refetchInterval: 7000,
    enabled: opts?.enabled ?? true,
  });
}

export function useAttentionCards(ws: string, q: AttentionListQuery, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: lhdKeys.attention(ws, q),
    queryFn: () => lhdApi.listAttention(q),
    placeholderData: (prev) => prev,
    // Shared queue: poll often enough that a card another AM just resolved disappears promptly.
    refetchInterval: 7000,
    enabled: opts?.enabled ?? true,
  });
}

export function useAttentionCard(ws: string, id: string | null) {
  return useQuery({
    queryKey: lhdKeys.attentionCard(ws, id ?? '∅'),
    queryFn: () => lhdApi.getAttentionCard(id as string),
    enabled: !!id,
  });
}

function useCardResolution(
  ws: string,
  fn: (cardId: string, version: number) => Promise<unknown>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, version }: { cardId: string; version: number }) => fn(cardId, version),
    // onSettled (not onSuccess): a LOST confirm race (409) still refreshes the queue + thread, so the
    // card another AM just resolved disappears for the loser instead of lingering as a stale pending
    // draft they could try to re-send. On success it does the same refresh.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'attention', ws] });
      void qc.invalidateQueries({ queryKey: ['lhd', 'attentionCard', ws] });
      // A card resolution sends / denies a draft; refresh the open conversation thread(s)
      // so the in-conversation approve UI (InboxPage) reflects the new disposition too.
      void qc.invalidateQueries({ queryKey: ['lhd', 'conversation', ws] });
      void qc.invalidateQueries({ queryKey: ['lhd', 'conversations', ws] });
    },
  });
}

export const useApproveCard = (ws: string) => useCardResolution(ws, lhdApi.approveCard);
export const useRejectCard = (ws: string) => useCardResolution(ws, lhdApi.rejectCard);
export const useTakeOverCard = (ws: string) => useCardResolution(ws, lhdApi.takeOverCard);
export const useCreateDsar = (ws: string) => useCardResolution(ws, lhdApi.createDsarFromCard);
export const useConfirmHandoff = (ws: string) => useCardResolution(ws, lhdApi.confirmHandoffFromCard);

export function useEditApprove(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, version, editedBody }: { cardId: string; version: number; editedBody: string }) =>
      lhdApi.editApproveCard(cardId, version, editedBody),
    // onSettled: an edited-send that lost the version race (someone else edited/resolved meanwhile)
    // re-syncs so the loser sees the current draft/disposition instead of overwriting blindly.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'attention', ws] });
      void qc.invalidateQueries({ queryKey: ['lhd', 'attentionCard', ws] });
      void qc.invalidateQueries({ queryKey: ['lhd', 'conversation', ws] });
      void qc.invalidateQueries({ queryKey: ['lhd', 'conversations', ws] });
    },
  });
}

/** Alias matching the in-conversation approve UI (#9) — edit a pending draft then send it. */
export const useEditApproveCard = (ws: string) => useEditApprove(ws);

export function useApproveLanguage(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, version, languageCode }: { cardId: string; version: number; languageCode: string }) =>
      lhdApi.approveLanguageFromCard(cardId, version, languageCode),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'attention', ws] });
    },
  });
}

export function useAttachAlias(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, version, personId }: { cardId: string; version: number; personId: string }) =>
      lhdApi.attachAliasFromCard(cardId, version, personId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'attention', ws] });
    },
  });
}

// ---------------------------------------------------------------------------
// Agents list + detail
// ---------------------------------------------------------------------------
export function useAgents(ws: string, q: AgentListQuery) {
  return useQuery({
    queryKey: lhdKeys.agents(ws, q),
    queryFn: () => lhdApi.listAgents(q),
    placeholderData: (prev) => prev,
  });
}

export function useAgent(ws: string, id: string | null) {
  return useQuery({
    queryKey: lhdKeys.agent(ws, id ?? '∅'),
    queryFn: () => lhdApi.getAgent(id as string),
    enabled: !!id,
  });
}

export function useCreateAgent(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAgentInput) => lhdApi.createAgent(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'agents', ws] });
    },
  });
}

export function useUpdateAgent(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateAgentInput }) => lhdApi.updateAgent(id, body),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'agents', ws] });
      void qc.invalidateQueries({ queryKey: lhdKeys.agent(ws, data.id) });
    },
  });
}

export function useArchiveAgent(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) => lhdApi.archiveAgent(id, version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'agents', ws] });
    },
  });
}

// ---------------------------------------------------------------------------
// Agent identity pickers — sending inbox (out) + rep users (iam)
// ---------------------------------------------------------------------------

/** Sending-inbox candidates for the agent's workspace. Disabled until a real id is known. */
export function useInboxes(workspaceId: string | null) {
  return useQuery({
    queryKey: lhdKeys.inboxes(workspaceId ?? '∅'),
    queryFn: () => lhdApi.listInboxes(workspaceId as string),
    enabled: !!workspaceId,
  });
}

/**
 * Rep-user candidates for the agent's workspace — client-portal sales reps
 * (role client_salesman), i.e. the only accounts that can actually open the rep
 * portal and see the handoff tasks keyed on rep_user_id. Workspace-scoped via
 * GET /api/iam/workspaces/:id/client-users. Disabled until a real id is known.
 */
export function useReps(workspaceId: string | null) {
  return useQuery({
    queryKey: lhdKeys.reps(workspaceId ?? '∅'),
    queryFn: () => lhdApi.listReps(workspaceId as string),
    enabled: !!workspaceId,
  });
}

export function usePatchInboxDisplayName(workspaceId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, displayName, version }: { id: string; displayName: string | null; version: number }) =>
      lhdApi.patchInboxDisplayName(id, displayName, version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lhdKeys.inboxes(workspaceId ?? '∅') });
    },
  });
}

// ---------------------------------------------------------------------------
// Test harness
// ---------------------------------------------------------------------------
export function useTestRuns(ws: string, agentId: string | null) {
  return useQuery({
    queryKey: lhdKeys.testRuns(ws, agentId ?? '∅'),
    queryFn: () => lhdApi.listTestRuns(agentId as string),
    enabled: !!agentId,
  });
}

export function useEstimateTestRun(agentId: string) {
  return useMutation({
    mutationFn: (exampleCount: number) => lhdApi.estimateTestRun(agentId, exampleCount),
  });
}

export function useCreateTestRun(ws: string, agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { examples: TestHarnessExample[]; confirmLargeRun?: boolean }) =>
      lhdApi.createTestRun(agentId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lhdKeys.testRuns(ws, agentId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Triage
// ---------------------------------------------------------------------------
export function useTriageItems(ws: string, q: TriageListQuery) {
  return useQuery({
    queryKey: lhdKeys.triage(ws, q),
    queryFn: () => lhdApi.listTriage(q),
    placeholderData: (prev) => prev,
  });
}

export function useMatchTriage(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MatchTriageInput }) => lhdApi.matchTriage(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'triage', ws] });
    },
  });
}

export function useDiscardTriage(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => lhdApi.discardTriage(id, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'triage', ws] });
    },
  });
}

export function useCreateConversationFromTriage(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MatchTriageInput }) =>
      lhdApi.createConversationFromTriage(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'triage', ws] });
      void qc.invalidateQueries({ queryKey: ['lhd', 'conversations', ws] });
    },
  });
}

// ---------------------------------------------------------------------------
// Scripts (conversation playbooks)
// ---------------------------------------------------------------------------
export function useScripts(ws: string, q: ScriptListQuery) {
  return useQuery({
    queryKey: lhdKeys.scripts(ws, q),
    queryFn: () => lhdApi.listScripts(q),
    placeholderData: (prev) => prev,
  });
}

export function useScript(ws: string, id: string | null) {
  return useQuery({
    queryKey: lhdKeys.script(ws, id ?? '∅'),
    queryFn: () => lhdApi.getScript(id as string),
    enabled: !!id,
  });
}

export function useCreateScript(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateScriptInput) => lhdApi.createScript(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'scripts', ws] });
    },
  });
}

export function useUpdateScript(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateScriptInput }) =>
      lhdApi.updateScript(id, body),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'scripts', ws] });
      void qc.invalidateQueries({ queryKey: lhdKeys.script(ws, data.id) });
    },
  });
}

export function useArchiveScript(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      lhdApi.archiveScript(id, version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'scripts', ws] });
    },
  });
}

/** Stage + branch mutations all invalidate the parent script tree (lhdKeys.script). */
function useTreeMutation<V>(ws: string, scriptId: string, fn: (vars: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lhdKeys.script(ws, scriptId) });
    },
  });
}

export const useCreateStage = (ws: string, scriptId: string) =>
  useTreeMutation(ws, scriptId, (body: CreateStageInput) => lhdApi.createStage(scriptId, body));

export const useUpdateStage = (ws: string, scriptId: string) =>
  useTreeMutation(ws, scriptId, ({ stageId, body }: { stageId: string; body: UpdateStageInput }) =>
    lhdApi.updateStage(scriptId, stageId, body),
  );

export const useDeleteStage = (ws: string, scriptId: string) =>
  useTreeMutation(ws, scriptId, ({ stageId, version }: { stageId: string; version: number }) =>
    lhdApi.deleteStage(scriptId, stageId, version),
  );

export const useCreateBranch = (ws: string, scriptId: string) =>
  useTreeMutation(ws, scriptId, ({ stageId, body }: { stageId: string; body: CreateBranchInput }) =>
    lhdApi.createBranch(scriptId, stageId, body),
  );

export const useUpdateBranch = (ws: string, scriptId: string) =>
  useTreeMutation(
    ws,
    scriptId,
    ({ stageId, branchId, body }: { stageId: string; branchId: string; body: UpdateBranchInput }) =>
      lhdApi.updateBranch(scriptId, stageId, branchId, body),
  );

export const useDeleteBranch = (ws: string, scriptId: string) =>
  useTreeMutation(
    ws,
    scriptId,
    ({ stageId, branchId, version }: { stageId: string; branchId: string; version: number }) =>
      lhdApi.deleteBranch(scriptId, stageId, branchId, version),
  );

// ---------------------------------------------------------------------------
// Agent documents (Phase 2 — outbound attachments library)
// ---------------------------------------------------------------------------
export function useDocuments(ws: string, q: AgentDocumentListQuery) {
  return useQuery({
    queryKey: lhdKeys.documents(ws, q),
    queryFn: () => lhdApi.listDocuments(q),
    placeholderData: (prev) => prev,
  });
}

export function useCreateDocument(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ meta, file }: { meta: CreateAgentDocumentMeta; file: File }) =>
      lhdApi.createDocument(meta, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'documents', ws] });
    },
  });
}

export function useUpdateDocument(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateAgentDocumentInput }) =>
      lhdApi.updateDocument(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'documents', ws] });
    },
  });
}

export function useArchiveDocument(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      lhdApi.archiveDocument(id, version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'documents', ws] });
    },
  });
}

export function useUnarchiveDocument(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      lhdApi.unarchiveDocument(id, version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'documents', ws] });
    },
  });
}

export function useDeleteDocument(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      lhdApi.deleteDocument(id, version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'documents', ws] });
    },
  });
}

// ----- Review-card document controls (attach / link) — refresh the conversation thread -----
export function useAttachDocumentToCard(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cardId,
      version,
      documentId,
    }: {
      cardId: string;
      version: number;
      documentId: string | null;
    }) => lhdApi.attachDocumentToCard(cardId, version, documentId),
    onSuccess: (action) => {
      void qc.invalidateQueries({ queryKey: lhdKeys.conversation(ws, action.conversationId) });
      void qc.invalidateQueries({ queryKey: ['lhd', 'attention', ws] });
    },
  });
}

export function useInsertDocumentLink(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cardId,
      version,
      documentId,
    }: {
      cardId: string;
      version: number;
      documentId: string;
    }) => lhdApi.insertDocumentLink(cardId, version, documentId),
    onSuccess: (action) => {
      void qc.invalidateQueries({ queryKey: lhdKeys.conversation(ws, action.conversationId) });
      void qc.invalidateQueries({ queryKey: ['lhd', 'attention', ws] });
    },
  });
}

// ---------------------------------------------------------------------------
// Bookings — outcome logging
// ---------------------------------------------------------------------------

export function useBookings(ws: string, q: BookingListQuery) {
  return useQuery({
    queryKey: lhdKeys.bookings(ws, q),
    queryFn: () => lhdApi.listBookings(q),
    placeholderData: (prev) => prev,
  });
}

export function useLogBookingOutcome(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: LogBookingOutcomeInput }) =>
      lhdApi.logBookingOutcome(id, body),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: ['lhd', 'bookings', ws] });
      void qc.invalidateQueries({ queryKey: lhdKeys.booking(ws, updated.id) });
    },
  });
}
